import { createRouter, createWebHistory } from 'vue-router';
import { verifyToken } from '@/api/auth';
import { clearToken } from '@/api/request';
import KnowledgeList from '../views/KnowledgeList.vue';
import KnowledgeForm from '../views/KnowledgeForm.vue';
import KnowledgeType from '../views/KnowledgeType.vue';
import KnowledgeVersionHistory from '../views/KnowledgeVersionHistory.vue';
import KnowledgeVersionCompare from '../views/KnowledgeVersionCompare.vue';
import KnowledgeRecycleBin from '../views/KnowledgeRecycleBin.vue';
import CollectionCondition from '../views/CollectionCondition.vue';
import CallStepManage from '../views/CallStepManage.vue';
import CallScheme from '../views/CallScheme.vue';
import OutputParamsList from '../views/OutputParamsList.vue';
import CallPlan from '../views/CallPlan.vue';
import ExecutionLogs from '../views/ExecutionLogs.vue';
import ExecutionPlanList from '../views/ExecutionPlanList.vue';
import ExecutionPlanDetail from '../views/ExecutionPlanDetail.vue';
import ExecutionLogDetail from '../views/ExecutionLogDetail.vue';
import ExecutionPlanStatistics from '../views/ExecutionPlanStatistics.vue';
import Config from '../views/Config.vue';

// 评估系统
import TestCaseList from '../views/EvalV2/TestCaseList.vue';
import TestCaseDetail from '../views/EvalV2/TestCaseDetail.vue';
import TestPlanList from '../views/EvalV2/TestPlanList.vue';
import TestPlanCreate from '../views/EvalV2/TestPlanCreate.vue';
import ExecutionResults from '../views/EvalV2/ExecutionResults.vue';
import ExecutionBatchDetail from '../views/EvalV2/ExecutionBatchDetail.vue';
import BatchComparison from '../views/EvalV2/BatchComparison.vue';
import ExecutionDetail from '../views/EvalV2/ExecutionDetail.vue';
import AnalysisReport from '../views/EvalV2/AnalysisReport.vue';
import ScoreTemplateManage from '../views/EvalV2/ScoreTemplateManage.vue';
import ModelComparisonList from '../views/EvalV2/ModelComparisonList.vue';
import ModelComparisonDetail from '../views/EvalV2/ModelComparisonDetail.vue';
import SszBenchmarkList from '../views/EvalV2/SszBenchmarkList.vue';
import SszBenchmarkDetail from '../views/EvalV2/SszBenchmarkDetail.vue';
import PosttreatmentRetestList from '../views/EvalV2/PosttreatmentRetestList.vue';
import PosttreatmentRetestDetail from '../views/EvalV2/PosttreatmentRetestDetail.vue';
import ApiManagement from '../views/ApiManagement.vue';
import ApiDocPage from '../views/ApiDocPage.vue';
import ApiTestPage from '../views/ApiTestPage.vue';
import PromptSimulator from '../views/PromptSimulator/index.vue';
import GenerateResponseSchema from '../views/PromptSimulator/GenerateResponseSchema.vue';
import GeneratePromptStructure from '../views/PromptSimulator/GeneratePromptStructure.vue';
import ReferenceDataStreamPage from '../views/ReferenceDataStreamPage.vue';
import ReferenceDataStreamLogList from '../views/ReferenceDataStreamLog/LogList.vue';
import ReferenceDataStreamLogDetail from '../views/ReferenceDataStreamLog/LogDetail.vue';
import Login from '../views/Login.vue';
import ExternalFrame from '../views/ExternalFrame.vue';
import QueueBoard from '../views/QueueBoard.vue';
import DiagnosisLogManage from '../views/DiagnosisLogManage.vue';
import DiagnosisDisplayStage from '../views/DiagnosisDisplayStage.vue';
import DiagnosisLogItem from '../views/DiagnosisLogItem.vue';
import WebSocketSessionReplay from '../views/WebSocketSessionReplay.vue';

// 系统文档
import DocsIndex from '../views/Docs/index.vue';
import DocsCategoryList from '../views/Docs/CategoryList.vue';
import DocsDetail from '../views/Docs/DocDetail.vue';
import FaultCodeExplanationLogList from '../views/FaultCodeExplanationLog/LogList.vue';
import FaultCodeExplanationLogDetail from '../views/FaultCodeExplanationLog/LogDetail.vue';
import PosttreatmentCheckLogList from '../views/PosttreatmentCheckLog/LogList.vue';
import PosttreatmentCheckLogDetail from '../views/PosttreatmentCheckLog/LogDetail.vue';

const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: { requiresAuth: false, title: '登录' }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { title: '首页' }
  },
  {
    path: '/knowledge',
    name: 'knowledge',
    component: KnowledgeList,
    meta: { title: '知识列表', roles: ['admin', 'editor'] }
  },
  {
    path: '/knowledge/create',
    name: 'knowledge-create',
    component: KnowledgeForm,
    meta: { title: '创建知识', roles: ['admin', 'editor'] }
  },
  {
    path: '/knowledge/edit/:id',
    name: 'knowledge-edit',
    component: KnowledgeForm,
    meta: { title: '编辑知识', roles: ['admin', 'editor'] }
  },
  {
    path: '/knowledge/:id/versions',
    name: 'knowledge-version-history',
    component: KnowledgeVersionHistory,
    meta: { title: '版本历史', roles: ['admin', 'editor'] }
  },
  {
    path: '/knowledge/:id/versions/compare',
    name: 'knowledge-version-compare',
    component: KnowledgeVersionCompare,
    meta: { title: '版本对比', roles: ['admin', 'editor'] }
  },
  {
    path: '/knowledge/recycle-bin',
    name: 'knowledge-recycle-bin',
    component: KnowledgeRecycleBin,
    meta: { title: '回收站', roles: ['admin', 'editor'] }
  },
  {
    path: '/knowledge-type',
    name: 'knowledge-type',
    component: KnowledgeType,
    meta: { title: '知识类型管理', roles: ['admin'] }
  },
  {
    path: '/collection-condition',
    name: 'collection-condition',
    component: CollectionCondition,
    meta: { title: '采集工况管理', roles: ['admin', 'editor'] }
  },
  {
    path: '/call-scheme',
    name: 'call-scheme',
    component: CallScheme,
    meta: { title: '调用方案', roles: ['admin', 'editor'] }
  },
  {
    path: '/call-scheme/:schemeId/steps',
    name: 'call-step-manage',
    component: CallStepManage,
    meta: { title: '步骤管理', roles: ['admin', 'editor'] }
  },
  {
    path: '/call-scheme/:schemeId/output-params',
    name: 'output-params-list',
    component: OutputParamsList,
    meta: { title: '出参列表', roles: ['admin', 'editor'] }
  },
  {
    path: '/diagnosis-log',
    name: 'diagnosis-log-manage',
    component: DiagnosisLogManage,
    meta: { title: 'AI调用文案管理' }
  },
  {
    path: '/diagnosis-log/stages',
    name: 'diagnosis-display-stage',
    component: DiagnosisDisplayStage,
    meta: { title: '展示阶段管理' }
  },
  {
    path: '/diagnosis-log/log-items',
    name: 'diagnosis-log-item',
    component: DiagnosisLogItem,
    meta: { title: '日志项管理' }
  },
  {
    path: '/diagnosis-log/replay',
    name: 'websocket-session-replay',
    component: WebSocketSessionReplay,
    meta: { title: '会话回放' }
  },
  {
    path: '/diagnosis-log/components',
    name: 'diagnosis-component-manage',
    component: () => import('../views/DiagnosisComponentManage.vue'),
    meta: { title: '组件管理' }
  },
  {
    path: '/call-plan',
    name: 'call-plan',
    component: CallPlan,
    meta: { title: '调用计划', roles: ['admin', 'editor'] }
  },
  {
    path: '/execution-logs',
    name: 'execution-logs',
    component: ExecutionLogs,
    meta: { title: '执行日志', roles: ['admin', 'technician'] }
  },
  {
    path: '/execution-plan-list',
    name: 'execution-plan-list',
    component: ExecutionPlanList,
    meta: { title: '执行计划管理', roles: ['admin', 'technician'] }
  },
  {
    path: '/execution-plan-detail/:id',
    name: 'execution-plan-detail',
    component: ExecutionPlanDetail,
    meta: { title: '执行计划详情', roles: ['admin', 'technician'] }
  },
  {
    path: '/execution-log-detail/:id',
    name: 'execution-log-detail',
    component: ExecutionLogDetail,
    meta: { title: '执行记录详情', roles: ['admin', 'technician'] }
  },
  {
    path: '/execution-plan-statistics',
    name: 'execution-plan-statistics',
    component: ExecutionPlanStatistics,
    meta: { title: 'AI调用统计报表', roles: ['admin', 'technician'] }
  },
  {
    path: '/fault-code-explanation-log',
    name: 'fault-code-explanation-log',
    component: FaultCodeExplanationLogList,
    meta: { title: '故障码解释日志' }
  },
  {
    path: '/fault-code-explanation-log/:id',
    name: 'fault-code-explanation-log-detail',
    component: FaultCodeExplanationLogDetail,
    meta: { title: '故障码解释日志详情' }
  },
  {
    path: '/posttreatment-check-log',
    name: 'posttreatment-check-log',
    component: PosttreatmentCheckLogList,
    meta: { title: '后处理屏蔽判断日志' }
  },
  {
    path: '/posttreatment-check-log/:id',
    name: 'posttreatment-check-log-detail',
    component: PosttreatmentCheckLogDetail,
    meta: { title: '后处理屏蔽判断日志详情' }
  },
  // 评估系统路由
  {
    path: '/eval-v2',
    redirect: '/eval-v2/test-cases'
  },
  {
    path: '/eval-v2/test-cases',
    name: 'eval-test-cases',
    component: TestCaseList,
    meta: { title: '评估用例' }
  },
  {
    path: '/eval-v2/test-case/:id',
    name: 'eval-test-case-detail',
    component: TestCaseDetail,
    meta: { title: '用例详情' }
  },
  {
    path: '/eval-v2/test-plans',
    name: 'eval-test-plans',
    component: TestPlanList,
    meta: { title: '评估计划' }
  },
  {
    path: '/eval-v2/test-plans/create',
    name: 'eval-test-plan-create',
    component: TestPlanCreate,
    meta: { title: '创建评估计划' }
  },
  {
    path: '/eval-v2/results',
    name: 'eval-results',
    component: ExecutionResults,
    meta: { title: '评估结果' }
  },
  {
    path: '/eval-v2/results/compare',
    name: 'eval-batch-compare',
    component: BatchComparison,
    meta: { title: '批次对比' }
  },
  {
    path: '/eval-v2/results/:id',
    name: 'eval-batch-detail',
    component: ExecutionBatchDetail,
    meta: { title: '批次详情' }
  },
  {
    path: '/eval-v2/execution/:id',
    name: 'eval-execution-detail',
    component: ExecutionDetail,
    meta: { title: '执行详情' }
  },
  {
    path: '/eval-v2/analysis',
    name: 'eval-analysis',
    component: AnalysisReport,
    meta: { title: '分析报告' }
  },
  {
    path: '/eval-v2/score-templates',
    name: 'eval-score-templates',
    component: ScoreTemplateManage,
    meta: { title: '评分标准管理' }
  },
  {
    path: '/eval-v2/model-comparison',
    name: 'model-comparison-list',
    component: ModelComparisonList,
    meta: { title: '模型横向对比' }
  },
  {
    path: '/eval-v2/model-comparison/:id',
    name: 'model-comparison-detail',
    component: ModelComparisonDetail,
    meta: { title: '对比计划详情' }
  },
  {
    path: '/eval-v2/ssz-benchmark',
    name: 'ssz-benchmark-list',
    component: SszBenchmarkList,
    meta: { title: '模型对比2.0' }
  },
  {
    path: '/eval-v2/ssz-benchmark/:id',
    name: 'ssz-benchmark-detail',
    component: SszBenchmarkDetail,
    meta: { title: '模型对比2.0运行详情' }
  },
  {
    path: '/eval-v2/posttreatment-retest',
    name: 'posttreatment-retest-list',
    component: PosttreatmentRetestList,
    meta: { title: '后处理屏蔽模型重复测试' }
  },
  {
    path: '/eval-v2/posttreatment-retest/:id',
    name: 'posttreatment-retest-detail',
    component: PosttreatmentRetestDetail,
    meta: { title: '后处理屏蔽模型重复测试详情' }
  },
  {
    path: '/config',
    name: 'config',
    component: Config,
    meta: { title: '系统配置' }
  },
  {
    path: '/api-management',
    name: 'api-management',
    component: ApiManagement,
    meta: { title: 'API管理' }
  },
  {
    path: '/api-management/:apiId/doc',
    name: 'api-doc',
    component: ApiDocPage,
    meta: { title: 'API文档' }
  },
  {
    path: '/api-management/:apiId/test',
    name: 'api-test',
    component: ApiTestPage,
    meta: { title: 'API测试' }
  },
  {
    path: '/simulator/prompt-generator',
    name: 'prompt-simulator',
    component: PromptSimulator,
    meta: { title: 'AI调用模拟' }
  },
  {
    path: '/simulator/generate-response-schema',
    name: 'generate-response-schema',
    component: GenerateResponseSchema,
    meta: { title: '生成响应结构' }
  },
  {
    path: '/simulator/generate-prompt-structure',
    name: 'generate-prompt-structure',
    component: GeneratePromptStructure,
    meta: { title: '生成Prompt结构' }
  },
  {
    path: '/tools/reference-data-stream',
    name: 'reference-data-stream',
    component: ReferenceDataStreamPage,
    meta: { title: '提取参考数据流' }
  },
  {
    path: '/tools/reference-data-stream/logs',
    name: 'reference-data-stream-logs',
    component: ReferenceDataStreamLogList,
    meta: { title: '参考数据流调用记录' }
  },
  {
    path: '/tools/reference-data-stream/logs/:id',
    name: 'reference-data-stream-log-detail',
    component: ReferenceDataStreamLogDetail,
    meta: { title: '调用记录详情' }
  },
  // 外部系统 iframe 嵌入
  {
    path: '/external/:page',
    name: 'external-frame',
    component: ExternalFrame,
    meta: { title: '外部系统' }
  },
  // BullMQ 队列管理（使用不同的路径避免与后端路由冲突）
  {
    path: '/queue-board',
    name: 'queue-board',
    component: QueueBoard,
    meta: { title: '队列管理' }
  },
  // 无权限页面
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('../views/Forbidden.vue'),
    meta: { requiresAuth: false, title: '无权限访问' }
  },
  // 用户管理（仅管理员）
  {
    path: '/users',
    name: 'user-manage',
    component: () => import('../views/UserManage.vue'),
    meta: { title: '用户管理', roles: ['admin'] }
  },
  // 系统文档
  {
    path: '/docs',
    name: 'docs-index',
    component: DocsIndex,
    meta: { title: '系统文档' }
  },
  {
    path: '/docs/:category',
    name: 'docs-category',
    component: DocsCategoryList,
    meta: { title: '文档分类' }
  },
  {
    path: '/docs/:category/:filename',
    name: 'docs-detail',
    component: DocsDetail,
    meta: { title: '文档详情' }
  },
  {
    path: '/model-call-monitor/stats',
    name: 'model-call-stats',
    component: () => import('../views/ModelCallMonitor/Stats.vue'),
    meta: { title: '模型调用统计', roles: ['admin'] }
  },
  {
    path: '/model-call-monitor/detail',
    name: 'model-call-detail',
    component: () => import('../views/ModelCallMonitor/Detail.vue'),
    meta: { title: '模型调用明细', roles: ['admin'] }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// 系统名称常量
const SYSTEM_TITLE = '维修知识管理系统';

// 路由守卫：检查登录状态
router.beforeEach(async (to, from, next) => {
  // 检查Token是否存在
  const token = localStorage.getItem('auth_token');
  const isLoggedIn = !!token;
  
  // 如果访问登录页且已登录，跳转到对应角色首页
  if (to.path === '/login' && isLoggedIn) {
    const userInfoStr = localStorage.getItem('auth_user_info');
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
    const role = userInfo?.role;
    if (role === 'technician') {
      next('/execution-plan-list');
    } else {
      next('/knowledge');
    }
    return;
  }
  
  // 如果访问需要认证的页面且未登录，跳转到登录页
  if (to.path !== '/login' && !isLoggedIn) {
    next('/login');
    return;
  }
  
  // 如果有Token，验证Token有效性（可选，避免每次请求都验证）
  if (token && to.path !== '/login') {
    try {
      await verifyToken();
    } catch (error) {
      // Token无效，清除登录状态并跳转到登录页
      clearToken();
      if (to.path !== '/login') {
        next('/login');
        return;
      }
    }
  }

  // 角色权限检查
  if (to.meta?.roles && isLoggedIn) {
    const userInfoStr = localStorage.getItem('auth_user_info');
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
    const role = userInfo?.role;
    if (!role || !to.meta.roles.includes(role)) {
      // 按角色跳转到对应的默认首页
      if (role === 'editor') {
        next('/knowledge');
      } else if (role === 'technician') {
        next('/execution-plan-list');
      } else {
        next('/login');
      }
      return;
    }
  }

  next();
});

// 路由守卫：动态更新页面标题
router.afterEach((to) => {
  const pageTitle = to.meta?.title;
  if (pageTitle) {
    document.title = `${pageTitle} - ${SYSTEM_TITLE}`;
  } else {
    document.title = SYSTEM_TITLE;
  }
});

export default router;
